import { useTranslation } from 'react-i18next';
import { Check, Palette } from 'lucide-react';
import { PrefSection, type PrefVariant } from '@/components/PrefSection';
import { useTheme } from '@/hooks/useTheme';
import { THEMES } from '@/lib/theme';
import { cn } from '@/lib/utils';

/**
 * Theme picker. Renders one tile per registered theme; clicking a tile applies
 * it immediately (no save button — the swap is visible the instant the body
 * class flips). Choice persists in `localStorage('ui-theme')` and is re-applied
 * on next page load by `initTheme()` in `main.tsx`.
 */
export const ThemeSwitcher = ({ variant = 'card' }: { variant?: PrefVariant }) => {
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();

  return (
    <PrefSection
      variant={variant}
      icon={Palette}
      title={t('Appearance')}
      description={t('Pick a color theme. Your choice is saved to this browser.')}
    >
      <div className="grid gap-2 sm:grid-cols-2">
        {THEMES.map((entry) => (
          <ThemeTile
            key={entry.id}
            entry={entry}
            active={theme === entry.id}
            onPick={() => setTheme(entry.id)}
            t={t}
          />
        ))}
      </div>
    </PrefSection>
  );
};

const ThemeTile = ({
  entry,
  active,
  onPick,
  t,
}: {
  entry: (typeof THEMES)[number];
  active: boolean;
  onPick: () => void;
  t: (k: string) => string;
}) => (
  <button
    type="button"
    onClick={onPick}
    aria-pressed={active}
    className={cn(
      'flex items-start gap-3 rounded-md border p-3 text-left transition-colors',
      active
        ? 'border-primary bg-primary/5'
        : 'border-border bg-surface hover:bg-muted',
    )}
  >
    <span
      aria-hidden
      className="mt-0.5 h-8 w-8 shrink-0 rounded-md border"
      style={{ background: entry.previewColor }}
    />
    <span className="flex min-w-0 flex-1 flex-col">
      <span className="flex items-center gap-1.5 text-sm font-medium">
        {t(entry.label)}
        {active && <Check className="h-3.5 w-3.5 text-primary" />}
      </span>
      <span className="text-[0.6875rem] text-muted-foreground">
        {t(entry.description)}
      </span>
    </span>
  </button>
);
